import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  ProvisionUserRequest,
  ProvisionUserResponse,
  Role,
  User,
} from "@/types/admin/users";
import type {
  ResourcePermissionsResponse,
  SetResourcePermissionsRequest,
} from "@/types/shared";
import { permissionKeys, roleKeys, userKeys } from "./keys";

// ── Helper de normalização ────────────────────────────────────────────────────
export function normalizeUser(user: User): User {
  const roleNames = (user.user_roles ?? [])
    .map((entry) => entry.role?.name)
    .filter((v): v is string => typeof v === "string" && v.length > 0);

  return {
    ...user,
    display_name: user.display_name ?? user.full_name,
    is_active: user.is_active ?? user.status === "active",
    identity_color: user.identity_color ?? "#111111",
    identity_text_color: user.identity_text_color ?? "#d9ed09",
    roles: user.roles ?? roleNames,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: async () => {
      const users = await api.get<User[]>("/users");
      return users.map(normalizeUser);
    },
    enabled: options?.enabled ?? true,
  });
}

/** Usuários ativos para dropdowns de atribuição. Acessível a todos os autenticados. */
export function useAssignableUsers() {
  return useQuery({
    queryKey: userKeys.assignees(),
    queryFn: async () => {
      const users = await api.get<User[]>("/users/assignees");
      return users.map(normalizeUser);
    },
  });
}

export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: async () => {
      const user = await api.get<User>("/users/me");
      return normalizeUser(user);
    },
    enabled: options?.enabled ?? true,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.all,
    queryFn: () => api.get<Role[]>("/roles"),
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useProvisionUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProvisionUserRequest) =>
      api.post<ProvisionUserResponse>("/users/provision", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      roleName,
    }: {
      userId: string;
      roleName: string;
    }) => api.post<User>(`/users/${userId}/set-role`, { role_name: roleName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      qc.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      status,
    }: {
      userId: string;
      status: "active" | "inactive";
    }) => api.patch<User>(`/users/${userId}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export interface UserUpdatePayload {
  full_name?: string;
  status?: "active" | "inactive";
  firebase_uid?: string;
  identity_color?: string;
  identity_text_color?: string;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UserUpdatePayload;
    }) => api.patch<User>(`/users/${userId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

// ── Firebase sync ─────────────────────────────────────────────────────────────

export interface FirebaseUnlinkedUser {
  firebase_uid: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
}

export interface FirebaseSyncResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export function useFirebaseUnlinked(enabled: boolean) {
  return useQuery({
    queryKey: userKeys.firebaseUnlinked(),
    queryFn: () =>
      api.get<FirebaseUnlinkedUser[]>("/users/firebase-unlinked"),
    enabled,
  });
}

export function useSyncFromFirebase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (users: FirebaseUnlinkedUser[]) =>
      api.post<FirebaseSyncResult>("/users/sync-from-firebase", { users }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      qc.invalidateQueries({ queryKey: userKeys.firebaseUnlinked() });
    },
  });
}

// ── Resource-level CRUD permissions ──────────────────────────────────────────

export function useUserResourcePermissions(userId: string | null | undefined) {
  return useQuery({
    queryKey: permissionKeys.byUser(userId),
    queryFn: () =>
      api.get<ResourcePermissionsResponse>(
        `/users/${userId}/resource-permissions`
      ),
    enabled: !!userId,
  });
}

export function useSetUserResourcePermissions(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SetResourcePermissionsRequest) =>
      api.put<ResourcePermissionsResponse>(
        `/users/${userId}/resource-permissions`,
        data
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionKeys.byUser(userId) });
    },
  });
}
